import { type CSSProperties, type MouseEvent, type ReactNode, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { Sparkline } from '../../components/charts/Sparkline.js'
import { ENTERPRISE_CONTACT_EMAIL, PLANS } from './plans.js'
import teamCelebration from '../../assets/team-celebration.png'
import teamLift from '../../assets/team-lift.png'
import climbingLadder from '../../assets/climbing-ladder.png'
import checklist from '../../assets/checklist.png'

type IconName =
  | 'bolt'
  | 'tasks'
  | 'check'
  | 'arrowRight'
  | 'checkCircle'
  | 'innovate'
  | 'warning'
  | 'shield'
  | 'trend'
  | 'people'

type RevealProps = {
  readonly children: ReactNode
  readonly delay?: number
  readonly className?: string
  readonly style?: CSSProperties
}

function Reveal({ children, delay = 0, className, style }: RevealProps) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true)
      return
    }

    const element = ref.current
    if (element === null) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry === undefined) return
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.01 },
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(14px)',
        transition: `opacity 600ms ease ${delay}ms, transform 600ms ease ${delay}ms`,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

function Wordmark({ size = 15, dim = false }: { readonly size?: number; readonly dim?: boolean }) {
  return (
    <span
      className="wordmark"
      style={{
        fontFamily: 'var(--font-display)',
        fontSize: size,
        color: dim ? 'var(--fg-muted)' : 'var(--fg)',
      }}
    >
      OVRSEE<span className="dot" />
    </span>
  )
}

function Chip({
  children,
  className = '',
  style,
}: {
  readonly children: ReactNode
  readonly className?: string
  readonly style?: CSSProperties
}) {
  return (
    <span className={className} style={style}>
      {children}
    </span>
  )
}

function Icon({
  name,
  size = 16,
  stroke = 1.8,
  style,
}: {
  readonly name: IconName
  readonly size?: number
  readonly stroke?: number
  readonly style?: CSSProperties
}) {
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: stroke,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  }

  switch (name) {
    case 'bolt':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={style}>
          <path {...common} d="M13 2 5 13h6l-1 9 9-12h-6l1-8Z" />
        </svg>
      )
    case 'tasks':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={style}>
          <rect {...common} x="4" y="5" width="16" height="14" rx="2" />
          <path {...common} d="M8 9h8M8 13h8M8 17h5" />
        </svg>
      )
    case 'check':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={style}>
          <path {...common} d="m5 12 4 4 10-10" />
        </svg>
      )
    case 'arrowRight':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={style}>
          <path {...common} d="M5 12h14" />
          <path {...common} d="m13 6 6 6-6 6" />
        </svg>
      )
    case 'checkCircle':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={style}>
          <circle {...common} cx="12" cy="12" r="9" />
          <path {...common} d="m8.5 12.5 2.6 2.6 4.8-5.8" />
        </svg>
      )
    case 'innovate':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={style}>
          <path {...common} d="M12 3 13.8 8.2 19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3Z" />
        </svg>
      )
    case 'warning':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={style}>
          <path {...common} d="M12 4 3 20h18L12 4Z" />
          <path {...common} d="M12 9v5" />
          <path {...common} d="M12 17.5h.01" />
        </svg>
      )
    case 'shield':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={style}>
          <path {...common} d="M12 3 19 6v5c0 4.5-2.8 7.9-7 10-4.2-2.1-7-5.5-7-10V6l7-3Z" />
        </svg>
      )
    case 'trend':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={style}>
          <path {...common} d="M4 17 10 11l4 4 6-8" />
          <path {...common} d="M14 7h6v6" />
        </svg>
      )
    case 'people':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={style}>
          <circle {...common} cx="9" cy="8" r="3" />
          <circle {...common} cx="16" cy="10" r="2.5" />
          <path {...common} d="M4 19c.7-3 3-5 5-5s4.3 2 5 5" />
          <path {...common} d="M13 19c.4-1.8 1.7-3.2 3.5-3.2 1.4 0 2.6.8 3.5 2.2" />
        </svg>
      )
  }

  return null
}

const navLink: CSSProperties = {
  color: 'var(--fg-2)',
  textDecoration: 'none',
  fontSize: 13,
  cursor: 'pointer',
}

const NOTIFS = [
  { icon: 'checkCircle' as const, tone: 'positive', strong: 'Amara submitted', text: 'Q2 review - 2h early.' },
  { icon: 'innovate' as const, tone: 'brand', strong: 'Idea adopted', text: 'Async standups for cross-tz teams.' },
  { icon: 'warning' as const, tone: 'warning', strong: 'Jordan flagged', text: 'Blocked - invoice export bug.' },
  { icon: 'shield' as const, tone: 'info', strong: 'Manager-only', text: 'Rankings never shown to employees.' },
] as const

function NotificationCycle() {
  const [index, setIndex] = useState(0)
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    const timeoutIds: number[] = []
    const intervalId = window.setInterval(() => {
      setVisible(false)
      const timeoutId = window.setTimeout(() => {
        setIndex((current) => (current + 1) % NOTIFS.length)
        setVisible(true)
      }, 400)
      timeoutIds.push(timeoutId)
    }, 3200)

    return () => {
      window.clearInterval(intervalId)
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId))
    }
  }, [])

  const notification = NOTIFS[index] ?? NOTIFS[0]!
  const toneBg = {
    positive: 'var(--positive-bg)',
    brand: 'var(--brand-soft)',
    warning: 'var(--warning-bg)',
    info: 'var(--info-bg)',
  }[notification.tone]
  const toneFg = {
    positive: 'var(--positive)',
    brand: 'var(--brand)',
    warning: 'var(--warning)',
    info: 'var(--info)',
  }[notification.tone]

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 24,
        right: -20,
        background: 'var(--surface)',
        borderRadius: 10,
        padding: '10px 14px',
        boxShadow: 'var(--shadow-lg)',
        border: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        maxWidth: 240,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(6px)',
        transition: 'opacity 400ms ease, transform 400ms ease',
      }}
    >
      <span
        style={{
          width: 28,
          height: 28,
          borderRadius: 999,
          background: toneBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: toneFg,
          flexShrink: 0,
        }}
      >
        <Icon name={notification.icon} size={14} />
      </span>
      <div style={{ fontSize: 11.5, lineHeight: 1.3 }}>
        <strong>{notification.strong}</strong> {notification.text}
      </div>
    </div>
  )
}

function MiniMetric({
  label,
  value,
  trend,
}: {
  readonly label: string
  readonly value: string
  readonly trend: readonly number[]
}) {
  const match = /^(\d+)(.*)$/.exec(value)
  const base = match !== null ? Number.parseInt(match[1]!, 10) : null
  const suffix = match !== null ? match[2] : ''
  const [display, setDisplay] = useState<number | string>(base ?? value)
  const [series, setSeries] = useState(trend)

  useEffect(() => {
    if (base === null) return

    const intervalId = window.setInterval(() => {
      setDisplay((current) => {
        const currentValue = typeof current === 'number' ? current : base
        const next = Math.max(base - 3, Math.min(base + 3, currentValue + Math.round((Math.random() - 0.5) * 4)))
        setSeries((previous) => [...previous.slice(1), next])
        return next
      })
    }, 1300)

    return () => window.clearInterval(intervalId)
  }, [base])

  return (
    <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, padding: 8 }}>
      <div className="eyebrow" style={{ fontSize: 9 }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <span className="tnum" style={{ fontSize: 18, fontFamily: 'var(--font-display)', fontWeight: 500 }}>
          {display}
          {suffix}
        </span>
        <Sparkline data={series} width={36} height={14} />
      </div>
    </div>
  )
}

function PlanCard({ plan }: { readonly plan: (typeof PLANS)[number] }) {
  const isEnterprise = plan.name === 'Enterprise'

  const CTA = isEnterprise ? (
    <a
      href={`mailto:${ENTERPRISE_CONTACT_EMAIL}`}
      className={plan.featured ? 'btn btn-primary' : 'btn'}
      style={{ justifyContent: 'center', padding: '9px 12px', textDecoration: 'none' }}
    >
      {plan.ctaLabel}
    </a>
  ) : (
    <Link
      to="/signup"
      className={plan.featured ? 'btn btn-primary' : 'btn'}
      style={{ justifyContent: 'center', padding: '9px 12px', textDecoration: 'none' }}
    >
      {plan.ctaLabel}
    </Link>
  )

  return (
    <div
      className="card"
      style={{
        padding: 28,
        borderColor: plan.featured ? 'var(--mauve-300)' : 'var(--border)',
        background: plan.featured ? 'linear-gradient(180deg, var(--brand-soft), var(--surface))' : 'var(--surface)',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        boxShadow: plan.featured ? 'var(--shadow-lg)' : 'var(--shadow-xs)',
        position: 'relative',
      }}
    >
      {plan.featured ? (
        <Chip className="chip chip-mauve" style={{ position: 'absolute', top: -10, right: 20, fontSize: 10 }}>
          Most popular
        </Chip>
      ) : null}
      <div className="eyebrow" style={{ color: plan.featured ? 'var(--mauve-700)' : 'var(--fg-muted)' }}>
        {plan.name}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span className="tnum" style={{ fontSize: 36, fontFamily: 'var(--font-display)', fontWeight: 500, letterSpacing: '-0.02em' }}>
          {plan.price}
        </span>
      </div>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--fg-muted)', lineHeight: 1.5 }}>{plan.description}</p>
      {CTA}
      <div style={{ height: 1, background: 'var(--divider)' }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {plan.features.map((feature) => (
          <div key={feature} style={{ display: 'flex', gap: 8, fontSize: 12.5, color: 'var(--fg-2)' }}>
            <Icon name="check" size={12} stroke={2} style={{ color: 'var(--positive)', marginTop: 3, flexShrink: 0 }} />
            <span>{feature}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function scrollTo(selector: string) {
  return (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    const root = event.currentTarget.closest('[data-marketing-root]')
    if (!(root instanceof HTMLElement)) return
    const target = root.querySelector(selector)
    if (!(target instanceof HTMLElement)) return
    root.scrollTo({
      top: target.offsetTop - 24,
      behavior: 'smooth',
    })
  }
}

export function Marketing() {
  return (
    <div data-marketing-root style={{ minHeight: '100%', overflow: 'auto', background: 'var(--bg)' }}>
      <style>{`
        [data-marketing-root] .marketing-shell {
          min-height: 100%;
        }

        [data-marketing-root] .marketing-hero,
        [data-marketing-root] .marketing-two-col,
        [data-marketing-root] .marketing-checklist,
        [data-marketing-root] .marketing-cta {
          display: grid;
        }

        @media (max-width: 960px) {
          [data-marketing-root] .marketing-hero,
          [data-marketing-root] .marketing-two-col,
          [data-marketing-root] .marketing-checklist,
          [data-marketing-root] .marketing-cta {
            grid-template-columns: 1fr !important;
          }

          [data-marketing-root] .marketing-features {
            grid-template-columns: 1fr 1fr !important;
          }
        }

        @media (max-width: 640px) {
          [data-marketing-root] nav,
          [data-marketing-root] .marketing-footer {
            padding-left: 20px !important;
            padding-right: 20px !important;
          }

          [data-marketing-root] section {
            padding-left: 20px !important;
            padding-right: 20px !important;
          }

          [data-marketing-root] .marketing-nav {
            flex-wrap: wrap;
            gap: 12px !important;
          }

          [data-marketing-root] .marketing-nav-links {
            margin-left: 0 !important;
            gap: 14px !important;
            flex-wrap: wrap;
          }

          [data-marketing-root] .marketing-nav-cta {
            margin-left: 0 !important;
            width: 100%;
            gap: 12px !important;
          }

          [data-marketing-root] .marketing-hero-title {
            font-size: 42px !important;
          }

          [data-marketing-root] .marketing-hero-actions,
          [data-marketing-root] .marketing-cta-actions {
            flex-direction: column !important;
            align-items: stretch !important;
          }

          /* The closing CTA card is a space-between row of text + actions. Flex
             min-width:auto stops either side shrinking below its content, so without
             this the actions get pushed past the card edge and clipped at phone
             widths — wrapping the INNER actions alone cannot save an outer row that
             never wraps. */
          [data-marketing-root] .marketing-cta-card {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 24px !important;
          }

          [data-marketing-root] .marketing-plan-grid {
            grid-template-columns: 1fr !important;
          }

          [data-marketing-root] .marketing-features {
            grid-template-columns: 1fr !important;
          }

          [data-marketing-root] .marketing-stat-card {
            left: 0 !important;
            right: auto !important;
            top: 16px !important;
            width: min(248px, calc(100vw - 40px));
          }
        }
      `}</style>

      <nav
        className="marketing-nav"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 24,
          padding: '16px 48px',
          borderBottom: '1px solid var(--border)',
          background: 'rgba(251,250,249,0.85)',
          backdropFilter: 'blur(8px)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Wordmark size={15} />
        <div className="marketing-nav-links" style={{ display: 'flex', gap: 22, marginLeft: 32, fontSize: 13, color: 'var(--fg-2)' }}>
          <a style={navLink} href="#product" onClick={scrollTo('#product')}>
            Product
          </a>
          <a style={navLink} href="#pricing" onClick={scrollTo('#pricing')}>
            Pricing
          </a>
          <a style={navLink} href="#how" onClick={scrollTo('#how')}>
            Resources
          </a>
        </div>
        <div className="marketing-nav-cta" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14 }}>
          <Link to="/login" style={navLink}>
            Sign in
          </Link>
          <Link
            to="/signup"
            className="btn btn-primary"
            style={{ padding: '8px 14px', fontSize: 13, textDecoration: 'none', whiteSpace: 'nowrap' }}
          >
            Start free trial
          </Link>
        </div>
      </nav>

      <section id="product" style={{ padding: '80px 48px 64px', maxWidth: 1240, margin: '0 auto' }}>
        <div className="marketing-hero" style={{ gridTemplateColumns: '1.1fr 1fr', gap: 56, alignItems: 'center' }}>
          <Reveal>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '5px 10px',
                borderRadius: 999,
                background: 'var(--brand-soft)',
                color: 'var(--mauve-700)',
                fontSize: 11.5,
                fontFamily: 'var(--font-mono)',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
              }}
            >
              <Icon name="bolt" size={11} /> Workforce efficiency, redefined
            </div>
            <h1 className="marketing-hero-title" style={{ fontFamily: 'var(--font-display)', fontSize: 60, fontWeight: 500, lineHeight: 1.02, letterSpacing: '-0.025em', margin: '20px 0 16px', color: 'var(--fg)' }}>
              Manage performance.
              <br />
              <span style={{ color: 'var(--brand)' }}>Not people.</span>
            </h1>
            <p style={{ fontSize: 17, color: 'var(--fg-2)', lineHeight: 1.5, maxWidth: 480, margin: 0 }}>
              Ovrsee gives managers honest, complexity-weighted insight into how their teams perform - without putting employees in a leaderboard war. Built for organisations who want clarity without surveillance.
            </p>
            <div className="marketing-hero-actions" style={{ display: 'flex', gap: 10, marginTop: 28, flexWrap: 'wrap' }}>
              <Link to="/signup" className="btn btn-primary" style={{ padding: '10px 16px', fontSize: 14, textDecoration: 'none' }}>
                Start free 14-day trial
              </Link>
              <a href="#pricing" onClick={scrollTo('#pricing')} className="btn" style={{ padding: '10px 16px', fontSize: 14, textDecoration: 'none' }}>
                <Icon name="tasks" size={12} /> View our flexible pricing
              </a>
            </div>
            <div style={{ display: 'flex', gap: 28, marginTop: 28, fontSize: 12, color: 'var(--fg-muted)', flexWrap: 'wrap' }}>
              <span>
                <Icon name="check" size={11} stroke={2} style={{ color: 'var(--positive)', marginRight: 4 }} />
                No credit card
              </span>
              <span>
                <Icon name="check" size={11} stroke={2} style={{ color: 'var(--positive)', marginRight: 4 }} />
                5-min setup
              </span>
              <span>
                <Icon name="check" size={11} stroke={2} style={{ color: 'var(--positive)', marginRight: 4 }} />
                Cancel anytime
              </span>
            </div>
          </Reveal>

          <Reveal delay={120} style={{ position: 'relative', aspectRatio: '1 / 1.1' }}>
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(160deg, var(--mauve-100), var(--mauve-50))',
                borderRadius: 18,
                border: '1px solid var(--mauve-200)',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
              }}
            >
              <img
                src={teamCelebration}
                alt="Team celebrating"
                width={736}
                height={704}
                style={{ width: '92%', height: 'auto', display: 'block', mixBlendMode: 'multiply' }}
              />
            </div>

            <style>{`@keyframes ovrseeFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-14px)}}`}</style>
            <div
              className="marketing-stat-card"
              style={{
                position: 'absolute',
                top: 20,
                left: -28,
                width: 248,
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 12,
                padding: 12,
                boxShadow: 'var(--shadow-lg)',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                animation: 'ovrseeFloat 5s ease-in-out infinite',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Wordmark size={10} dim />
                <Chip className="chip chip-mauve" style={{ fontSize: 9 }}>
                  OPERATIONS
                </Chip>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                <MiniMetric label="Efficiency" value="87" trend={[78, 82, 84, 85, 87]} />
                <MiniMetric label="On-time" value="88%" trend={[80, 82, 84, 86, 88]} />
              </div>
            </div>

            <NotificationCycle />
          </Reveal>
        </div>
      </section>

      <section style={{ padding: '72px 48px', background: 'var(--surface)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
        <div className="marketing-two-col" style={{ maxWidth: 1240, margin: '0 auto', gridTemplateColumns: '1fr 1.1fr', gap: 64, alignItems: 'center' }}>
          <Reveal
            style={{
              position: 'relative',
              background: 'var(--mauve-200)',
              borderRadius: 18,
              overflow: 'hidden',
              aspectRatio: '1 / 1.2',
            }}
          >
            <style>{`@keyframes ovrseeLift{0%,100%{transform:translateY(0) scale(1.0)}45%{transform:translateY(-8px) scale(1.015)}60%{transform:translateY(-6px) scale(1.01)}}`}</style>
            <img
              src={teamLift}
              alt="Team helping each other up"
              loading="lazy"
              width={564}
              height={685}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                animation: 'ovrseeLift 6s cubic-bezier(0.45,0,0.2,1) infinite',
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: 18,
                bottom: 18,
                background: 'rgba(255,255,255,0.94)',
                borderRadius: 10,
                padding: '8px 12px',
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--mauve-700)',
                letterSpacing: '0.05em',
              }}
            >
              COACHING - NOT SURVEILLANCE
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="eyebrow" style={{ color: 'var(--mauve-700)' }}>
              Why Ovrsee
            </div>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 38,
                fontWeight: 500,
                letterSpacing: '-0.02em',
                margin: '8px 0 16px',
                lineHeight: 1.1,
              }}
            >
              The team rises together.
              <br />
              The data stays with you.
            </h2>
            <p style={{ fontSize: 15, color: 'var(--fg-2)', lineHeight: 1.6, margin: '0 0 24px', maxWidth: 540 }}>
              Performance scores, rankings, and trend lines are visible to managers only - never to peers. Ovrsee gives you the truth about how the work is going, so you can coach the people who need it and stretch the people who don't.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, maxWidth: 520 }}>
              {[
                { t: 'Manager-only data', b: 'Employees never see scores or rankings.' },
                { t: 'Complexity-weighted', b: 'Harder work counts for more, automatically.' },
                { t: 'External delays excluded', b: "Bad data doesn't penalise good people." },
                { t: 'Audit trail on everything', b: 'Every action is logged, end-to-end.' },
              ].map((item) => (
                <div key={item.t} style={{ display: 'flex', gap: 8 }}>
                  <Icon name="check" size={13} stroke={2} style={{ color: 'var(--positive)', marginTop: 4, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 500 }}>{item.t}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 2 }}>{item.b}</div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section style={{ padding: '80px 48px' }}>
        <div style={{ maxWidth: 1240, margin: '0 auto' }}>
          <Reveal>
            <div className="eyebrow" style={{ color: 'var(--mauve-700)' }}>
              Built for managers
            </div>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 36,
                fontWeight: 500,
                letterSpacing: '-0.02em',
                margin: '8px 0 48px',
                maxWidth: 700,
              }}
            >
              Everything a team lead needs. Nothing that gets in the way.
            </h2>
          </Reveal>
          <div className="marketing-features" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
            {[
              { icon: 'tasks', title: 'Smart task assignment', body: 'See current workload before you assign. Set complexity weights. Save anything as a template.' },
              { icon: 'trend', title: 'Honest analytics', body: 'Complexity-weighted scoring, on-time rates, first-time pass rates. External delays excluded automatically.' },
              { icon: 'shield', title: 'Manager-only data', body: 'Performance data is invisible to employees. No leaderboards. No internal comparisons. Coaching, not surveillance.' },
              { icon: 'innovate', title: 'Innovation prompts', body: 'Post a quarterly question. Collect ideas privately. Reward what gets adopted.' },
              { icon: 'people', title: 'Multi-level hierarchy', body: 'Sub-managers run their teams. Super managers see across all of them. Audit logs included.' },
              { icon: 'bolt', title: 'Auto-escalation', body: 'Overdue tasks escalate to you automatically. Blocked tasks ping you in real time. No surprises.' },
            ].map((feature, index) => (
              <Reveal key={feature.title} delay={index * 60} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: 'var(--brand-soft)',
                    color: 'var(--brand)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon name={feature.icon as IconName} size={18} />
                </span>
                <h3
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontWeight: 500,
                    fontSize: 17,
                    margin: '8px 0 0',
                  }}
                >
                  {feature.title}
                </h3>
                <p style={{ margin: 0, fontSize: 13.5, color: 'var(--fg-muted)', lineHeight: 1.55 }}>{feature.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="how" style={{ padding: '80px 48px', background: 'var(--surface-2)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 1240, margin: '0 auto' }}>
          <div className="marketing-two-col" style={{ gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }}>
            <Reveal>
              <div className="eyebrow" style={{ color: 'var(--mauve-700)' }}>
                How Ovrsee climbs with you
              </div>
              <h2
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 38,
                  fontWeight: 500,
                  letterSpacing: '-0.02em',
                  margin: '8px 0 32px',
                  lineHeight: 1.1,
                }}
              >
                Built rung by rung,
                <br />
                so progress is visible.
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                {[
                  {
                    n: '01',
                    t: 'Set the rungs',
                    b: 'Assign tasks with priority, complexity, and a real deadline. Templates do the repetitive ones for you.',
                  },
                  {
                    n: '02',
                    t: 'Watch the climb',
                    b: 'Live workload heatmaps and countdowns show where each person is - without needing to ask.',
                  },
                  {
                    n: '03',
                    t: 'Reward the reach',
                    b: 'Complexity weighting means a stretch task counts. Rankings stay private; coaching stays public.',
                  },
                ].map((step) => (
                  <div key={step.n} style={{ display: 'grid', gridTemplateColumns: '44px 1fr', gap: 14, alignItems: 'flex-start' }}>
                    <span
                      className="mono"
                      style={{
                        fontSize: 12,
                        color: 'var(--mauve-700)',
                        letterSpacing: '0.08em',
                        fontWeight: 500,
                        padding: '4px 8px',
                        background: 'var(--brand-soft)',
                        borderRadius: 6,
                        textAlign: 'center',
                      }}
                    >
                      {step.n}
                    </span>
                    <div>
                      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 18 }}>{step.t}</div>
                      <div style={{ fontSize: 13.5, color: 'var(--fg-muted)', lineHeight: 1.55, marginTop: 4 }}>{step.b}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal
              delay={140}
              style={{
                borderRadius: 18,
                overflow: 'hidden',
                border: '1px solid var(--mauve-300)',
                background: 'var(--mauve-200)',
                aspectRatio: '1 / 1.15',
                position: 'relative',
              }}
            >
              <img
                src={climbingLadder}
                alt="Climbing the ladder together"
                loading="lazy"
                width={700}
                height={823}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </Reveal>
          </div>

          <div className="marketing-checklist" style={{ marginTop: 64, gridTemplateColumns: '1fr 1.2fr', gap: 56, alignItems: 'center' }}>
            <Reveal
              style={{
                borderRadius: 18,
                overflow: 'hidden',
                background: 'var(--mauve-100)',
                border: '1px solid var(--mauve-200)',
                aspectRatio: '1.25 / 1',
              }}
            >
              <img
                src={checklist}
                alt="Reviewing a checklist"
                loading="lazy"
                width={736}
                height={593}
                style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', mixBlendMode: 'multiply' }}
              />
            </Reveal>
            <Reveal delay={120}>
              <div className="eyebrow" style={{ color: 'var(--mauve-700)' }}>
                Review without the back-and-forth
              </div>
              <h2
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 32,
                  fontWeight: 500,
                  letterSpacing: '-0.02em',
                  margin: '8px 0 16px',
                  lineHeight: 1.15,
                }}
              >
                Approve, return, or extend - in two clicks.
              </h2>
              <p style={{ fontSize: 15, color: 'var(--fg-2)', lineHeight: 1.6, margin: '0 0 18px', maxWidth: 520 }}>
                Every submission lands in one queue with attachments, history, and the audit trail. Mark it satisfactory, send it back with notes, or flag an external delay - the score adjusts itself.
              </p>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <Chip className="chip chip-pos">Satisfactory</Chip>
                <Chip className="chip chip-warn">Return for correction</Chip>
                <Chip className="chip chip-mauve">External delay</Chip>
                <Chip className="chip">Extend deadline</Chip>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section id="pricing" style={{ padding: '80px 48px', background: 'var(--bg)' }}>
        <div style={{ maxWidth: 1240, margin: '0 auto' }}>
          <Reveal style={{ textAlign: 'center', marginBottom: 40 }}>
            <div className="eyebrow" style={{ color: 'var(--mauve-700)' }}>
              Pricing
            </div>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 36,
                fontWeight: 500,
                letterSpacing: '-0.02em',
                margin: '8px 0 8px',
              }}
            >
              Simple plans that scale with you.
            </h2>
            <p style={{ fontSize: 14, color: 'var(--fg-muted)', margin: 0 }}>14-day free trial - no credit card required.</p>
          </Reveal>
          <div className="marketing-plan-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, alignItems: 'stretch' }}>
            {PLANS.map((plan, index) => (
              <Reveal key={plan.name} delay={index * 80}>
                <PlanCard plan={plan} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '60px 48px 80px' }}>
        <Reveal
          className="card marketing-cta-card"
          style={{
            maxWidth: 1080,
            margin: '0 auto',
            padding: '48px',
            background: 'linear-gradient(135deg, var(--mauve-700), var(--mauve-800))',
            borderColor: 'transparent',
            color: 'var(--fg-on-mauve)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 32,
          }}
        >
          <div>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 32,
                fontWeight: 500,
                letterSpacing: '-0.02em',
                margin: '0 0 8px',
              }}
            >
              Run a clearer, fairer team.
            </h2>
            <p style={{ margin: 0, opacity: 0.85, fontSize: 14 }}>Set up in five minutes. Cancel any time.</p>
          </div>
          <div className="marketing-cta-actions" style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <Link to="/login" style={{ color: 'var(--fg-on-mauve)', opacity: 0.85, textDecoration: 'none', fontSize: 13.5, fontWeight: 500 }}>
              Sign in
            </Link>
            <Link
              to="/signup"
              className="btn"
              style={{
                padding: '12px 20px',
                fontSize: 14,
                background: 'var(--surface)',
                color: 'var(--mauve-700)',
                borderColor: 'var(--surface)',
                textDecoration: 'none',
              }}
            >
              Start free 14-day trial <Icon name="arrowRight" size={14} />
            </Link>
          </div>
        </Reveal>
      </section>

      <footer
        className="marketing-footer"
        style={{
          padding: '24px 48px 40px',
          borderTop: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          fontSize: 11.5,
          color: 'var(--fg-muted)',
          flexWrap: 'wrap',
        }}
      >
        <Wordmark size={11} dim />
        <span>© 2026 Ovrsee, Inc.</span>
        <div style={{ flex: 1 }} />
        <Link to="/login" style={navLink}>
          Sign in
        </Link>
        <a style={navLink}>Privacy</a>
        <a style={navLink}>Terms</a>
        <a style={navLink}>Security</a>
        <a style={navLink}>Contact</a>
      </footer>
    </div>
  )
}
